/**
 * Mobile port of frontend/client/src/utilities.js markdownToHtml.
 *
 * Exact same regex pipeline as the web — produces identical HTML output so the
 * WebView renderer in MarkdownBody.tsx can use the same .markdown-body CSS.
 *
 * Dependencies removed / inlined:
 *   • phoneFormatter  → simple inline formatter
 *   • convertTimestampsToReadable → inlined below
 */

// ─── Phone number helpers ─────────────────────────────────────────────────────

const formatUSPhoneNumberPretty = (digits: string): string => {
  const clean = digits.replace(/\D/g, '').replace(/^1/, '');
  if (clean.length === 10) {
    return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
  }
  return digits;
};

// ─── Timestamp helpers ───────────────────────────────────────────────────────

const convertTimestampsToReadable = (text: string): string => {
  const isoDateTimeRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/g;
  const isoDateOnlyRegex = /\b\d{4}-\d{2}-\d{2}\b(?!T)/g;

  const fmtDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const fmtDateTime = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  text = text.replace(isoDateTimeRegex, (match) => {
    try { return fmtDateTime.format(new Date(match)); } catch { return match; }
  });
  text = text.replace(isoDateOnlyRegex, (match) => {
    try { return fmtDate.format(new Date(`${match}T00:00:00`)); } catch { return match; }
  });
  return text;
};

// ─── ICD-10-CM code auto-linking ──────────────────────────────────────────────
// Orchestrator care-plan summaries cite diagnosis codes inline ("ADHD (F90)",
// "E11.9"). Turn each into a tap to its public reference page. Scoped to the
// strict ICD-10-CM shape (a letter + 2 digits/AB, optional .subcode) and skipped
// inside existing links / code / pre spans and tags, so we never double-wrap or
// touch markup. A short denylist drops the common non-ICD collision (vitamin
// B12); anything rarer that slips through resolves to the page's graceful
// "code not found" search. ICD links open eleplan.com/icd10/<code> via the same
// anchor onPress that handles every other link.
const ICD10_CODE_RE = /\b[A-Z][0-9][0-9AB](?:\.[0-9A-Z]{1,4})?\b/g;
const ICD10_DENYLIST = new Set(['B12']);
// Capturing group → String.split keeps these skip-spans at ODD indices.
const ICD10_SKIP_RE = /(<a\b[^>]*>[\s\S]*?<\/a>|<code>[\s\S]*?<\/code>|<pre>[\s\S]*?<\/pre>|<[^>]+>)/g;

export function linkifyIcd10Codes(html: string): string {
  return html
    .split(ICD10_SKIP_RE)
    .map((part, i) => {
      if (i % 2 === 1) return part; // an <a>/<code>/<pre> span or a bare tag — leave it
      return part.replace(ICD10_CODE_RE, (m) =>
        ICD10_DENYLIST.has(m.toUpperCase())
          ? m
          : `<a href="https://eleplan.com/icd10/${encodeURIComponent(m)}" rel="noopener noreferrer" target="_blank">${m}</a>`
      );
    })
    .join('');
}

// ─── markdownToHtml ───────────────────────────────────────────────────────────

export const markdownToHtml = (
  markdown: string,
  options: { streaming?: boolean } = {}
): string => {
  if (!markdown || typeof markdown !== 'string') return '';
  const streaming = options.streaming === true;

  // Repair a common malformed anchor fragment seen in model output where only
  // URL + rel/target attributes remain (opening <a ... is truncated), e.g.:
  //   https://example.com" rel="noopener noreferrer" target="_blank">Label
  // Convert to Markdown link so the normal link pipeline handles it cleanly.
  markdown = markdown.replace(
    /((?:https?:\/\/|www\.)[^\s"<>]+)"\s+rel="noopener noreferrer"\s+target="_blank"\s*>\s*([^\n<]+)/gi,
    (_m, rawUrl, rawLabel) => {
      const url = String(rawUrl || '').trim();
      const label = String(rawLabel || '').trim();
      if (!url) return _m;
      const href = url.startsWith('www.') ? `https://${url}` : url;
      // If label is empty, just keep the URL text.
      if (!label) return href;
      return `[${label}](${href})`;
    }
  );

  // Tables FIRST
  markdown = markdown.replace(
    /^[ \t]*(\|[^\n]+\|)[ \t]*\n[ \t]*(\|[-:| ]+\|)[ \t]*(?:\n((?:[ \t]*\|[^\n]*\|[ \t]*\n?)*))?/gm,
    (match, headerLine, separatorLine, bodyLines = '') => {
      const body = (bodyLines || '').trim();
      const hasCompleteRow =
        body && body.split('\n').some((line: string) => line.trim() && line.includes('|'));
      if (streaming && !hasCompleteRow) {
        return '<div class="streaming-table-placeholder">Loading table…</div>';
      }

      const processCellContent = (str = '') => {
        if (!str) return '';
        str = str.replace(/\+1(\d{10})/g, (_, d) => formatUSPhoneNumberPretty(d));
        let out = str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        out = out
          .replace(/&lt;(a)\s+([^&]*?)&gt;/gi, '<$1 $2>')
          .replace(/&lt;\/(a)&gt;/gi, '</$1>')
          .replace(/&lt;(\/?)(strong|em|u|s|code|mark|br|ul|ol|li|p|hr)(\s*\/?)&gt;/gi, '<$1$2$3>');
        return out;
      };

      const headerCells = headerLine.trim().split('|').filter(Boolean);
      const numColumns = headerCells.length;
      const alignmentCells = separatorLine.trim().split('|').filter(Boolean);
      const alignments = alignmentCells.map((cell: string) => {
        const t = cell.trim();
        if (/^:-+:$/.test(t)) return 'center';
        if (/^:-+/.test(t)) return 'left';
        if (/-+:$/.test(t)) return 'right';
        return 'left';
      });
      const headers = headerCells
        .map((h: string, i: number) => `<th style="text-align:${alignments[i] || 'left'}">${processCellContent(h.trim())}</th>`)
        .join('');
      let rows = '';
      if (body) {
        const rowLines = body.split('\n').filter((line: string) => line.trim() && line.includes('|'));
        rows = rowLines
          .map((row: string) => {
            const rawCells = row.trim().split('|').filter(Boolean);
            const cells: string[] = [];
            for (let i = 0; i < numColumns; i++) {
              cells.push(rawCells[i] !== undefined ? rawCells[i] : '');
            }
            return `<tr>${cells.map((c, i) => `<td style="text-align:${alignments[i] || 'left'}">${processCellContent(c.trim())}</td>`).join('')}</tr>`;
          })
          .join('');
      }
      return `<div style="overflow-x:auto;max-width:100%;-webkit-overflow-scrolling:touch;"><table><thead><tr>${headers}</tr></thead>${rows ? `<tbody>${rows}</tbody>` : ''}</table></div>`;
    }
  );

  // Horizontal rules
  markdown = markdown.replace(/^[-*_]{3,}$/gm, '<hr>');

  // Code blocks
  markdown = markdown.replace(/```(?:\w*\n)?([\s\S]*?)```/g, (_, code) => {
    return `<pre><code>${code.trim()}</code></pre>`;
  });

  // Inline code
  markdown = markdown.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Blockquotes
  markdown = markdown.replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>');

  // Headers
  markdown = markdown.replace(/^#{1,6} (.*)$/gm, (full, content) => {
    const level = full.match(/^#+/)![0].length;
    return `<h${level}>${content}</h${level}>`;
  });

  // Bold
  markdown = markdown.replace(/\*\*(.*?)\*\*/g, (_, content) => {
    if (content.endsWith(':')) return `<strong class="strong-title">${content}</strong>`;
    return `<strong>${content}</strong>`;
  });

  // Italic
  markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, '<em>$1</em>');

  // Underline
  markdown = markdown.replace(/__(.*?)__/g, '<u>$1</u>');

  // Strikethrough
  markdown = markdown.replace(/~~(.*?)~~/g, '<s>$1</s>');

  // Images
  markdown = markdown.replace(
    /!\[(.*?)\]\((\S+?)(?:\s+"(.*?)")?\)/g,
    (_, alt, url, title) => {
      const ta = title ? ` title="${title}"` : '';
      return `<a target="_blank" rel="noopener noreferrer" href="${url}"${ta}><img src="${url}" alt="${alt}"${ta} /></a>`;
    }
  );

  // Standalone links
  markdown = markdown.replace(
    /^(\s*)\[(.*?)\]\((.*?)\)(\s*)$/gm,
    '<a href="$3" rel="noopener noreferrer" target="_blank" class="standalone-link">$2</a>'
  );
  // Inline links
  markdown = markdown.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" rel="noopener noreferrer" target="_blank">$1</a>'
  );

  // Auto-link URLs
  markdown = markdown.replace(
    /(?<!<a [^>]*href="|<code>|href="|src=")((?:https?:\/\/|www\.)[^\s<>"]+)/gi,
    (match) => {
      const url = match.startsWith('www.') ? `https://${match}` : match;
      const clean = url.replace(/[.,;:!?)\]]+$/, '');
      const trail = url.slice(clean.length);
      return `<a href="${clean}" rel="noopener noreferrer" target="_blank">${clean}</a>${trail}`;
    }
  );

  // ICD-10-CM codes → tap to the public reference page. Runs AFTER the link
  // passes so it skips codes already inside an <a> (and inside <code>/<pre>).
  markdown = linkifyIcd10Codes(markdown);

  // Phone numbers
  markdown = markdown.replace(/\+1(\d{10})/g, (_, d) => formatUSPhoneNumberPretty(d));
  markdown = markdown.replace(
    /\b(\d{3})[-.]?(\d{3})[-.]?(\d{4})\b/g,
    (match, a, b, c) => {
      if (match.includes('(')) return match;
      return formatUSPhoneNumberPretty(a + b + c);
    }
  );

  // Ordered list items that are just bold headers — convert to paragraphs
  markdown = markdown.replace(/^\d+\.\s+(<strong>.*?<\/strong>)\s*$/gm, '<p>$1</p>');

  // Unordered lists — two-level nesting + inline bullet glyph.
  //
  // Two levels because caregiver-summary responses routinely nest
  // sub-bullets under a section parent ("**Medical Emergencies:**" +
  // bulleted sub-items). The prior regex flattened every match into
  // siblings of a single `<ul>` — sub-items lost their relationship
  // to their parent and the indentation disappeared from the
  // rendered output. Two levels covers the 95th-percentile case.
  //
  // Inline bullet via `<strong>●</strong>` because RNRH's default
  // marker is positioned OUTSIDE the li's flex box and sits outside
  // the pill's background. A prior `<span>` injection failed (the
  // markup rendered as literal text in some RNRH/RNW builds —
  // likely a stream-chunking parse error on a partial `<span` token).
  // `<strong>` is reliable: it's in `tagsStyles` with brand primary
  // color, so the glyph anchors INSIDE the pill and inherits the
  // tint automatically. No new class-style required.
  markdown = markdown.replace(/((?:^(?: {0,8})?[-*+]\s+.*$\n?)+)/gm, (match) => {
    const lines = match
      .trim()
      .split('\n')
      .map((line: string) => {
        const m = line.match(/^( *)[-*+]\s+(.+)$/);
        return m ? { indent: m[1].length, content: m[2].trim() } : null;
      })
      .filter((x): x is { indent: number; content: string } => x !== null);

    if (lines.length === 0) return match;

    const baseIndent = lines[0].indent;
    // U+2022 BULLET (`•`), not U+25CF BLACK CIRCLE (`●`).
    //
    // Why: `●` has its baseline at the bottom of the visible glyph,
    // so it baseline-aligns LOW against the surrounding body text.
    // Centering it requires `vertical-align: middle` — which RNW
    // silently drops from RN style objects (confirmed via DevTools:
    // computed style on the rendered span shows `vertical-align:
    // baseline` even when `verticalAlign: 'middle'` is set in
    // tagsStyles / classesStyles / baseStyle). #981, #983, #985 all
    // chased this property through different layers without it ever
    // reaching the DOM. Dead code.
    //
    // U+2022 is the standard typographic bullet — fonts design its
    // glyph to sit at the x-height middle by default, no vertical-
    // align needed. Verified live in the dev DOM by overriding the
    // textContent and screenshotting: bullet centered with body text.
    //
    // `<strong class="li-bullet">` retained: `<strong>` gives the
    // brand primary color, the class adds `marginRight: 10` (the
    // only style RNW actually applies to the bullet span).
    // Three bullet variants:
    //   - TOP : top-level items, slightly larger glyph so parent
    //     rows ("Communication:", "Daily Living:") read as section
    //     headers within the chip stack.
    //   - SUB : nested sub-items, body-sized glyph.
    //   - LINK: document-link rows (the chip's body is a single
    //     `<a href>` — e.g. "Document Links → poor-audio.ogg"). Swaps
    //     the typographic bullet for a paperclip glyph so the chip
    //     reads as "downloadable attachment" rather than a list
    //     bullet. Detection runs on the post-anchor-conversion HTML
    //     (anchor regex above turns `[name](url)` into `<a …>name</a>`
    //     BEFORE this list pass).
    const BULLET_TOP = '<strong class="li-bullet-top">•</strong>';
    const BULLET_SUB = '<strong class="li-bullet">•</strong>';
    const BULLET_LINK = '<strong class="li-bullet-link">📎</strong>';

    // Single-anchor li: content is exactly one `<a …>text</a>` with
    // optional trailing punctuation (model sometimes adds a period
    // or comma after the link).
    const LINK_ONLY_RE = /^<a [^>]*>[^<]*<\/a>[.,;:\s]*$/;

    // Inline style on the nested ul so sub-items visibly indent under
    // their parent pill. Outer ul gets no left padding (pills hug the
    // message-bubble edge); only the nested ul shifts right.
    const NESTED_UL_OPEN = '<ul style="margin-left:20px;margin-top:6px;margin-bottom:0;">';

    let html = '<ul>';
    let inNested = false;
    let openTopLi = false;
    for (const item of lines) {
      const nested = item.indent >= baseIndent + 2;
      const isLink = LINK_ONLY_RE.test(item.content.trim());
      const bullet = isLink ? BULLET_LINK : (nested ? BULLET_SUB : BULLET_TOP);
      if (nested) {
        if (!inNested) { html += NESTED_UL_OPEN; inNested = true; }
        html += `<li>${bullet}${item.content}</li>`;
      } else {
        if (inNested) { html += '</ul></li>'; inNested = false; openTopLi = false; }
        if (openTopLi) html += '</li>';
        html += `<li>${bullet}${item.content}`;
        openTopLi = true;
      }
    }
    if (inNested) html += '</ul></li>';
    else if (openTopLi) html += '</li>';
    html += '</ul>';
    return html;
  });

  // Fallback table pass
  markdown = markdown.replace(
    /(\|[^\n]+\|)\s*\n\s*(\|[-:| ]+\|)\s*\n((?:\s*\|[^\n]+\|\s*\n?)+)/g,
    (match, headerLine, separatorLine, bodyLines) => {
      if (match.includes('<table>')) return match;
      const processCellContent = (str = '') => {
        let out = str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        out = out
          .replace(/&lt;(a)\s+([^&]*?)&gt;/gi, '<$1 $2>')
          .replace(/&lt;\/(a)&gt;/gi, '</$1>')
          .replace(/&lt;(\/?)(strong|em|u|s|code|mark|br|ul|ol|li|p|hr)(\s*\/?)&gt;/gi, '<$1$2$3>');
        return out;
      };
      const headerCells = headerLine.trim().split('|').slice(1, -1);
      const alignments = separatorLine
        .trim()
        .split('|')
        .slice(1, -1)
        .map((cell: string) => {
          const t = cell.trim();
          if (/^:-+:$/.test(t)) return 'center';
          if (/^:-+/.test(t)) return 'left';
          if (/-+:$/.test(t)) return 'right';
          return 'left';
        });
      const headers = headerCells
        .map((h: string, i: number) => `<th style="text-align:${alignments[i] || 'left'}">${processCellContent(h.trim())}</th>`)
        .join('');
      const body = (bodyLines || '').trim();
      const rowLines = body ? body.split('\n').filter((line: string) => line.trim().startsWith('|')) : [];
      const rows = rowLines
        .map((row: string) => {
          const cells = row.trim().split('|').slice(1, -1);
          return `<tr>${cells.map((c: string, i: number) => `<td style="text-align:${alignments[i] || 'left'}">${processCellContent(c.trim())}</td>`).join('')}</tr>`;
        })
        .join('');
      return `<div style="overflow-x:auto;max-width:100%;-webkit-overflow-scrolling:touch;"><table><thead><tr>${headers}</tr></thead>${rows ? `<tbody>${rows}</tbody>` : ''}</table></div>`;
    }
  );

  // Paragraphs.
  // The outer regex already skips lines that begin with block-level tags. The
  // inner callback's previous `startsWith('<')` check was too coarse — it also
  // skipped lines that begin with INLINE tags like <strong>, <em>, <a>, which
  // are produced by the bold/italic/link passes above. Those lines must still
  // be wrapped in <p> so paragraph spacing actually applies; otherwise rationale
  // strings of the form `**Label:** text\n\n**Label:** text` collapse inline.
  // We now only skip block-tag opens / closes, plus list-item lines.
  const BLOCK_TAG_OPEN_OR_CLOSE = /^<\/?(h\d|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th|hr|div|p)\b/i;
  markdown = markdown.replace(
    /^(?!<\/?(h\d|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th|hr|div|p)\b)(?!\s*$)(.+)$/gm,
    (match) => {
      const trimmed = match.trim();
      if (trimmed === '' || /^\s*[-*+]\s/.test(trimmed)) return match;
      if (BLOCK_TAG_OPEN_OR_CLOSE.test(trimmed)) return match;
      return `<p>${trimmed}</p>`;
    }
  );

  // Sanitise stray tags
  markdown = markdown.replace(
    /<(?!\/?(a|img|code|pre|strong|em|u|s|blockquote|ul|ol|li|p|h\d|table|tr|td|th|tbody|thead|hr|mark|br|div)\b)[^>]*>/gi,
    (m) => m.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  );

  return convertTimestampsToReadable(markdown);
};
